/**
 * User Profile Page Component
 * Redesigned to match EmployeeDetailsPage visual language:
 * Hero header with gradient, stat cards, SectionCards — no tabs.
 */

import React, { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  Button,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  InputAdornment,
  IconButton,
  CircularProgress,
  Alert,
  Tooltip,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Person,
  Email,
  Badge,
  Phone,
  Security,
  Business,
  Lock,
  CalendarToday,
  Edit as EditIcon,
  ArrowBack,
  ExpandMore,
  Visibility,
  VisibilityOff,
  CheckCircle,
  VpnKey,
  WhatsApp,
  LinkOff,
} from '@mui/icons-material';
import { useAuth, useAuthActions } from '../../stores/authStore';
import { usePermissions } from '../../hooks/usePermissions';
import { useListaPermitida } from '../../authorization/useListaPermitida';
import { authService } from '../../services/authService';
import { userService } from '../../services/userService';
import {
  UserBinding,
  BINDING_ENTITY_TYPE_LABELS,
  formatCPF,
  formatWhatsApp,
  getBindingFunctionLabel,
  getRoleLabel,
} from '../../types/user';
import {
  PageShell,
  IdentityBanner,
  IdentifierChip,
  StatCard,
  SectionCard,
  InfoItem,
} from '../../components';
import { useSnackbar } from 'notistack';

/* ══════════════════════════════════════════════════════════════ */

const UserProfilePage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { setUser } = useAuthActions();
  const { permissions } = usePermissions();
  const { enqueueSnackbar } = useSnackbar();


  // Edit profile dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', whatsapp: '' });
  const [editLoading, setEditLoading] = useState(false);

  // Change password dialog
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordFormErrors, setPasswordFormErrors] = useState<Record<string, string>>({});
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  // Ler o vínculo é ler usuário, e a maioria dos perfis não lê usuário: a tela é a própria
  // conta, mas esta lista não é. Antes daqui a recusa virava alerta vermelho de "erro ao
  // carregar vínculos", que é exatamente o que ela não é.
  const carregarVinculos = useCallback(
    () => userService.getUserBindings(Number(user?.id)),
    [user?.id]
  );
  const vinculos = useListaPermitida<UserBinding>('user.read', 'Vínculos', carregarVinculos, {
    habilitado: Boolean(user?.id),
  });
  const bindings = vinculos.itens;
  const loadingBindings = vinculos.carregando;

  // A lista é a do contexto ativo, não a do login: é o que a pessoa pode onde ela está agora.
  const groupedPermissions = useMemo(() => {
    if (!permissions.length) return {};
    return permissions.reduce((groups, perm) => {
      const dotIndex = perm.indexOf('.');
      const resource = dotIndex > -1 ? perm.substring(0, dotIndex) : perm;
      const action = dotIndex > -1 ? perm.substring(dotIndex + 1) : '';
      if (!groups[resource]) groups[resource] = [];
      if (action) groups[resource].push(action);
      return groups;
    }, {} as Record<string, string[]>);
  }, [permissions]);

  const permissionCount = permissions.length;
  const permissionGroupCount = Object.keys(groupedPermissions).length;

  // --- Edit Profile ---
  const handleOpenEditDialog = useCallback(() => {
    if (!user) return;
    setEditForm({ name: user.name, whatsapp: user.whatsapp ?? '' });
    setEditDialogOpen(true);
  }, [user]);

  const handleSaveProfile = async () => {
    if (!user?.id || !editForm.name.trim()) return;
    setEditLoading(true);
    try {
      await userService.updateUser(Number(user.id), {
        name: editForm.name.trim(),
        whatsapp: editForm.whatsapp.trim() || undefined,
      });
      const refreshedUser = await authService.getProfile();
      setUser(refreshedUser);
      setEditDialogOpen(false);
      enqueueSnackbar('Perfil atualizado com sucesso', { variant: 'success' });
    } catch (error: any) {
      enqueueSnackbar(error.message ?? 'Erro ao atualizar perfil', { variant: 'error' });
    } finally {
      setEditLoading(false);
    }
  };

  // --- Change Password ---
  const handleOpenPasswordDialog = () => {
    setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setPasswordError(null);
    setPasswordFormErrors({});
    setPasswordSuccess(false);
    setShowCurrentPassword(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
    setPasswordDialogOpen(true);
  };

  const validatePasswordForm = (): boolean => {
    const errors: Record<string, string> = {};
    if (!passwordForm.currentPassword) {
      errors.currentPassword = 'Senha atual é obrigatória';
    }
    if (!passwordForm.newPassword) {
      errors.newPassword = 'Nova senha é obrigatória';
    } else if (passwordForm.newPassword.length < 8) {
      errors.newPassword = 'A senha deve ter pelo menos 8 caracteres';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(passwordForm.newPassword)) {
      errors.newPassword = 'A senha deve conter letras maiúsculas, minúsculas e números';
    } else if (passwordForm.newPassword === passwordForm.currentPassword) {
      errors.newPassword = 'A nova senha deve ser diferente da senha atual';
    }
    if (!passwordForm.confirmPassword) {
      errors.confirmPassword = 'Confirmação de senha é obrigatória';
    } else if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      errors.confirmPassword = 'As senhas não coincidem';
    }
    setPasswordFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChangePassword = async (event: React.FormEvent) => {
    event.preventDefault();
    setPasswordError(null);
    if (!validatePasswordForm()) return;
    setPasswordLoading(true);
    try {
      await authService.changePassword({
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
        confirmPassword: passwordForm.confirmPassword,
      });
      setPasswordSuccess(true);
      enqueueSnackbar('Senha alterada com sucesso! Faça login novamente.', { variant: 'success' });
      setTimeout(async () => {
        await logout();
      }, 2000);
    } catch (err: any) {
      setPasswordError(err.message ?? 'Erro ao alterar senha');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handlePasswordInputChange = (field: keyof typeof passwordForm) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setPasswordForm((prev) => ({ ...prev, [field]: event.target.value }));
    if (passwordFormErrors[field]) {
      setPasswordFormErrors((prev) => ({ ...prev, [field]: '' }));
    }
    if (passwordError) setPasswordError(null);
  };

  if (!user) {
    return (
      <PageShell
        breadcrumbs={[{ label: 'Início', path: '/' }, { label: 'Meu Perfil' }]}
      >
        <Box sx={{ textAlign: 'center', py: 1 }}>
          <CircularProgress />
        </Box>
      </PageShell>
    );
  }

  return (
    <PageShell
      breadcrumbs={[{ label: 'Início', path: '/' }, { label: 'Meu Perfil' }]}
      actions={
        <>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ArrowBack />}
            onClick={() => navigate(-1)}
          >
            Voltar
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<EditIcon />}
            onClick={handleOpenEditDialog}
          >
            Editar Perfil
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Lock />}
            onClick={() => navigate('/profile/mfa')}
          >
            Segurança MFA
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<Lock />}
            onClick={handleOpenPasswordDialog}
          >
            Alterar Senha
          </Button>
        </>
      }
    >
        {/* ═══ IDENTIDADE ═══ */}
        <IdentityBanner
          avatar={
            user.avatarPath ? (
              <Box
                component="img"
                src={user.avatarPath}
                alt=""
                sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : (
              user.name.charAt(0).toUpperCase()
            )
          }
          name={user.name}
          status={
            user.roles?.length ? (
              <Box display="flex" gap={0.5} flexWrap="wrap">
                {user.roles.map((role) => (
                  <Chip key={role} label={getRoleLabel(role)} size="small" color="primary" />
                ))}
              </Box>
            ) : undefined
          }
          context={user.email}
          identifiers={
            <>
              <Tooltip title="Copiar CPF">
                <Box
                  sx={{ display: 'flex', cursor: 'pointer' }}
                  onClick={() => {
                    navigator.clipboard.writeText(user.cpf);
                    enqueueSnackbar('CPF copiado!', { variant: 'info', autoHideDuration: 1500 });
                  }}
                >
                  <IdentifierChip icon={<Badge sx={{ fontSize: 16 }} />} label={formatCPF(user.cpf)} />
                </Box>
              </Tooltip>

              {user.whatsapp && (
                <Tooltip title={`WhatsApp: ${formatWhatsApp(user.whatsapp)}`}>
                  <IconButton
                    size="small"
                    color="success"
                    href={`https://wa.me/55${user.whatsapp.replace(/\D/g, '')}`}
                    target="_blank"
                    sx={{
                      bgcolor: alpha('#25D366', 0.1),
                      '&:hover': { bgcolor: alpha('#25D366', 0.2) },
                    }}
                  >
                    <WhatsApp fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}

              <Tooltip title={user.email}>
                <IconButton
                  size="small"
                  color="primary"
                  href={`mailto:${user.email}`}
                  sx={{
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.2) },
                  }}
                >
                  <Email fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          }
        />

        {/* ═══ STAT CARDS ═══ */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 6, sm: 3 }}>
            <StatCard
              icon={<VpnKey fontSize="small" />}
              label="Tipo de Usuário"
              value={user.userType === 'admin' ? 'Administrador' : 'Profissional'}
              color={theme.palette.info.main}
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <StatCard
              icon={<Security fontSize="small" />}
              label="Papéis"
              value={user.roles?.length ? `${user.roles.length} ${user.roles.length === 1 ? 'papel' : 'papéis'}` : 'Nenhum'}
              color={theme.palette.primary.main}
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <StatCard
              icon={<Business fontSize="small" />}
              label="Vínculos"
              value={loadingBindings ? '...' : `${bindings.length} ${bindings.length === 1 ? 'vínculo' : 'vínculos'}`}
              color={theme.palette.secondary.main}
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <StatCard
              icon={<CalendarToday fontSize="small" />}
              label="Membro desde"
              value={user.createdAt ? new Date(user.createdAt).toLocaleDateString('pt-BR') : '—'}
              color={theme.palette.success.main}
            />
          </Grid>
        </Grid>

        {/* ═══ DETAIL SECTIONS ═══ */}
        <Grid container spacing={2.5} sx={{ mb: 3 }}>
          {/* Personal Info */}
          <Grid size={{ xs: 12, md: 6 }}>
            <SectionCard
              title="Informações Pessoais"
              icon={<Person fontSize="small" />}
              accentColor={theme.palette.primary.main}
            >
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <InfoItem
                    icon={<Person sx={{ fontSize: 14 }} />}
                    label="Nome"
                    value={user.name}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <InfoItem
                    icon={<Email sx={{ fontSize: 14 }} />}
                    label="E-mail"
                    value={user.email}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <InfoItem
                    icon={<Badge sx={{ fontSize: 14 }} />}
                    label="CPF"
                    value={formatCPF(user.cpf)}
                  />
                </Grid>
                {user.whatsapp && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <InfoItem
                      icon={<Phone sx={{ fontSize: 14 }} />}
                      label="WhatsApp"
                      value={formatWhatsApp(user.whatsapp)}
                    />
                  </Grid>
                )}
              </Grid>
            </SectionCard>
          </Grid>

          {/* Bindings */}
          <Grid size={{ xs: 12, md: 6 }}>
            <SectionCard
              title="Vínculos"
              icon={<Business fontSize="small" />}
              accentColor={theme.palette.secondary.main}
            >
              {loadingBindings ? (
                <Box>
                  {[1, 2].map((i) => (
                    <Paper key={i} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
                      <Skeleton variant="text" width="50%" />
                      <Skeleton variant="text" width="70%" />
                    </Paper>
                  ))}
                </Box>
              ) : bindings.length > 0 ? (
                <Box display="flex" flexDirection="column" gap={1}>
                  {bindings.map((binding) => (
                    <Paper
                      key={binding.id}
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        bgcolor: alpha(theme.palette.secondary.main, 0.02),
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                        <Business sx={{ fontSize: 18, color: 'text.secondary' }} />
                        <Typography variant="body2" fontWeight={700} noWrap>
                          {binding.entityDetails?.name || `ID: ${binding.entityId}`}
                        </Typography>
                      </Box>
                      <Box display="flex" gap={0.5} flexWrap="wrap" mb={0.5}>
                        <Chip
                          label={BINDING_ENTITY_TYPE_LABELS[binding.entityType]}
                          size="small"
                          variant="outlined"
                        />
                        <Chip
                          label={binding.functionLabel || getBindingFunctionLabel(binding.function)}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                        <Chip
                          label={binding.status === 'active' ? 'Ativo' : 'Inativo'}
                          size="small"
                          color={binding.status === 'active' ? 'success' : 'default'}
                        />
                      </Box>
                      {binding.entityDetails?.cnpj && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          CNPJ: {binding.entityDetails.cnpj}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary" display="block">
                        Desde: {new Date(binding.startedAt).toLocaleDateString('pt-BR')}
                      </Typography>
                    </Paper>
                  ))}
                </Box>
              ) : (
                <Box textAlign="center" py={2}>
                  <LinkOff sx={{ fontSize: 36, color: 'text.disabled', mb: 0.5 }} />
                  <Typography variant="body2" color="text.secondary">
                    {vinculos.bloqueio ?? vinculos.erro ?? 'Nenhum vínculo encontrado'}
                  </Typography>
                </Box>
              )}
            </SectionCard>
          </Grid>
        </Grid>

        {/* ═══ PERMISSIONS — full width, multi-column ═══ */}
        <SectionCard
          title={`Permissões (${permissionCount})`}
          icon={<Security fontSize="small" />}
          accentColor={theme.palette.info.main}
          action={
            user.roles?.length ? (
              <Box display="flex" gap={0.5} flexWrap="wrap">
                {user.roles.map((role) => (
                  <Chip
                    key={role}
                    label={getRoleLabel(role)}
                    size="small"
                    color="primary"
                    variant="outlined"
                    icon={<Security sx={{ fontSize: '14px !important' }} />}
                  />
                ))}
              </Box>
            ) : undefined
          }
        >
          {permissionGroupCount > 0 ? (
            <Box
              sx={{
                columnCount: { xs: 1, sm: 2, md: 3 },
                columnGap: 2,
              }}
            >
              {Object.entries(groupedPermissions)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([resource, actions]) => (
                  <Accordion
                    key={resource}
                    disableGutters
                    elevation={0}
                    sx={{
                      breakInside: 'avoid',
                      border: '1px solid',
                      borderColor: 'divider',
                      mb: 0.5,
                      '&::before': { display: 'none' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMore />}
                      sx={{ minHeight: 36, '& .MuiAccordionSummary-content': { my: 0.5 } }}
                    >
                      <Typography variant="body2" fontWeight={500}>
                        {resource}
                      </Typography>
                      <Chip
                        label={actions.length}
                        size="small"
                        sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                      />
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0, pb: 1 }}>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {actions.sort().map((action) => (
                          <Chip
                            key={`${resource}.${action}`}
                            label={action}
                            size="small"
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                ))}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Nenhuma permissão atribuída
            </Typography>
          )}
        </SectionCard>

      {/* ═══ DIALOGS ═══ */}

      {/* Edit Profile Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => !editLoading && setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Editar Perfil</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Nome"
            value={editForm.name}
            onChange={(e) => setEditForm((prev) => ({ ...prev, name: e.target.value }))}
            disabled={editLoading}
            sx={{ mt: 1, mb: 2 }}
          />
          <TextField
            fullWidth
            label="WhatsApp"
            value={editForm.whatsapp}
            onChange={(e) => setEditForm((prev) => ({ ...prev, whatsapp: e.target.value }))}
            disabled={editLoading}
            placeholder="(00) 00000-0000"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} disabled={editLoading}>
            Cancelar
          </Button>
          <Button
            onClick={handleSaveProfile}
            variant="contained"
            disabled={editLoading || !editForm.name.trim()}
          >
            {editLoading ? <CircularProgress size={20} /> : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog
        open={passwordDialogOpen}
        onClose={() => !passwordLoading && !passwordSuccess && setPasswordDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Alterar Senha</DialogTitle>
        <DialogContent>
          {passwordSuccess ? (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Senha Alterada!
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Sua senha foi alterada com sucesso. Você será redirecionado para o login.
              </Typography>
              <CircularProgress size={24} sx={{ mt: 2 }} />
            </Box>
          ) : (
            <Box component="form" id="change-password-form" onSubmit={handleChangePassword} noValidate>
              {passwordError && (
                <Alert severity="error" sx={{ mb: 2, mt: 1 }} onClose={() => setPasswordError(null)}>
                  {passwordError}
                </Alert>
              )}
              <TextField
                fullWidth
                type={showCurrentPassword ? 'text' : 'password'}
                label="Senha Atual"
                value={passwordForm.currentPassword}
                onChange={handlePasswordInputChange('currentPassword')}
                error={!!passwordFormErrors.currentPassword}
                helperText={passwordFormErrors.currentPassword}
                disabled={passwordLoading}
                autoComplete="current-password"
                autoFocus
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        edge="end"
                        disabled={passwordLoading}
                      >
                        {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ mt: 1, mb: 2 }}
              />
              <TextField
                fullWidth
                type={showNewPassword ? 'text' : 'password'}
                label="Nova Senha"
                value={passwordForm.newPassword}
                onChange={handlePasswordInputChange('newPassword')}
                error={!!passwordFormErrors.newPassword}
                helperText={
                  passwordFormErrors.newPassword ||
                  'Mínimo 8 caracteres com letras maiúsculas, minúsculas e números'
                }
                disabled={passwordLoading}
                autoComplete="new-password"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        edge="end"
                        disabled={passwordLoading}
                      >
                        {showNewPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                type={showConfirmPassword ? 'text' : 'password'}
                label="Confirmar Nova Senha"
                value={passwordForm.confirmPassword}
                onChange={handlePasswordInputChange('confirmPassword')}
                error={!!passwordFormErrors.confirmPassword}
                helperText={passwordFormErrors.confirmPassword}
                disabled={passwordLoading}
                autoComplete="new-password"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        edge="end"
                        disabled={passwordLoading}
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          )}
        </DialogContent>
        {!passwordSuccess && (
          <DialogActions>
            <Button onClick={() => setPasswordDialogOpen(false)} disabled={passwordLoading}>
              Cancelar
            </Button>
            <Button
              type="submit"
              form="change-password-form"
              variant="contained"
              disabled={passwordLoading}
            >
              {passwordLoading ? <CircularProgress size={20} /> : 'Alterar Senha'}
            </Button>
          </DialogActions>
        )}
      </Dialog>
    </PageShell>
  );
};

export default UserProfilePage;
